import {
  ArrowRightIcon,
  EnvelopeSimpleIcon,
  ShieldCheckIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useId, useState, type SyntheticEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ROUTES } from '@shared/config/routes';
import { Badge, Button, Skeleton } from '@shared/ui';
import { Checkbox } from '@shared/ui/Checkbox/Checkbox';
import { TextField } from '@shared/ui/TextField/TextField';

import type { LegalDocument } from '../api';
import { authErrorKey } from '../model/authErrors';
import { isProbablyEmail } from '../model/email';

import styles from './AuthFlow.module.css';

interface EmailStepProps {
  titleId: string;
  /** `h1` на странице входа, `h2` в модальном окне. */
  headingLevel?: 'h1' | 'h2' | undefined;
  email: string;
  onEmailChange: (value: string) => void;
  legalDocuments: LegalDocument[];
  /** Пока документы грузятся, отправка блокируется: иначе согласие потеряется. */
  legalPending: boolean;
  submitting: boolean;
  error: unknown;
  onSubmit: (input: { email: string; legalDocumentIds: string[] }) => void;
}

export function EmailStep({
  titleId,
  headingLevel = 'h2',
  email,
  onEmailChange,
  legalDocuments,
  legalPending,
  submitting,
  error,
  onSubmit,
}: EmailStepProps) {
  // Тег заголовка выбирает вызывающий экран: см. `headingLevel`.
  const Heading = headingLevel;
  const { t } = useTranslation();
  const formId = useId();
  const [accepted, setAccepted] = useState(false);
  // Две отметки вместо одной общей. Ошибка поля появляется, когда пользователь
  // от него ушёл; ошибка согласия — только после попытки отправки. Иначе уход
  // из поля адреса дорисовывал бы текст под чекбоксом, форма в центрированном
  // окне подрастала и уезжала вверх — прямо под курсором, между нажатием и
  // отпусканием кнопки мыши. Такой клик браузер не засчитывает.
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const trimmed = email.trim();
  const emailInvalid = trimmed.length > 0 && !isProbablyEmail(trimmed);
  const consentRequired = legalDocuments.length > 0;
  const consentMissing = consentRequired && !accepted;

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    setEmailTouched(true);
    setSubmitted(true);
    if (!isProbablyEmail(trimmed) || consentMissing || legalPending) return;
    onSubmit({
      email: trimmed.toLowerCase(),
      legalDocumentIds: legalDocuments.map((document) => document.id),
    });
  };

  const emailError =
    emailTouched && trimmed.length === 0
      ? t('auth.email.required')
      : emailTouched && emailInvalid
        ? t('auth.email.invalid')
        : undefined;

  return (
    <form className={styles.step} onSubmit={handleSubmit} noValidate>
      <header className={styles.header}>
        {/* Надзаголовок синий, а не красный: красный на экране один — у кнопки
            отправки (DESIGN_SYSTEM §2.2, §2.3). */}
        <Badge tone="action" icon={<ShieldCheckIcon size={14} />} uppercase>
          {t('auth.eyebrow')}
        </Badge>
        <Heading id={titleId} className={styles.title}>
          {t('auth.email.title')}
        </Heading>
        <p className={styles.lead}>{t('auth.email.lead')}</p>
      </header>

      <div className={styles.fields}>
        <TextField
          id={`${formId}-email`}
          label={t('auth.email.label')}
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          autoCapitalize="none"
          placeholder={t('auth.email.placeholder')}
          value={email}
          disabled={submitting}
          error={emailError}
          icon={<EnvelopeSimpleIcon size={18} />}
          onChange={(event) => {
            onEmailChange(event.target.value);
          }}
          onBlur={() => {
            setEmailTouched(true);
          }}
        />

        {/* Пока список редакций едет, место под чекбокс держит скелет: иначе
            форма подпрыгивает под курсором ровно в тот момент, когда человек
            целится в поле или в кнопку. Скелет повторяет геометрию того, что
            заменяет (DESIGN_SYSTEM §5.9): флажок 22 px с радиусом своей
            ступени лестницы — --radius-checkbox. */}
        {legalPending ? (
          <div className={styles.consentSkeleton} aria-hidden="true">
            <Skeleton width="22px" height="22px" radius="var(--radius-checkbox)" />
            <Skeleton height="1.1rem" />
          </div>
        ) : consentRequired ? (
          <Checkbox
            className={styles.consent}
            checked={accepted}
            disabled={submitting}
            error={submitted && consentMissing ? t('auth.email.consentRequired') : undefined}
            onChange={(event) => {
              setAccepted(event.target.checked);
            }}
          >
            <Trans
              i18nKey="auth.email.consent"
              components={{
                terms: <Link to={ROUTES.terms} target="_blank" rel="noreferrer" />,
                privacy: <Link to={ROUTES.privacy} target="_blank" rel="noreferrer" />,
              }}
            />
          </Checkbox>
        ) : null}
      </div>

      {error ? (
        <p className={styles.alert} role="alert">
          <WarningCircleIcon size={16} aria-hidden="true" className={styles.alertIcon} />
          <span>{t(authErrorKey(error))}</span>
        </p>
      ) : null}

      {/* Единственная красная кнопка окна (DESIGN_SYSTEM §5.1). */}
      <Button
        type="submit"
        size="lg"
        fullWidth
        disabled={submitting || legalPending}
        iconEnd={<ArrowRightIcon size={18} />}
      >
        {submitting ? t('auth.email.submitting') : t('auth.email.submit')}
      </Button>

      <p className={styles.footnote}>{t('auth.email.footnote')}</p>
    </form>
  );
}
